/**
 * Fiche d'intervention CERFA 15497*04 (arrêté du 29 mai 2024) : contenu saisi,
 * familles de fluide et règles réglementaires imprimées sur le formulaire
 * officiel (cases 7 à 9, signature du détenteur).
 *
 * Les seuils reprennent ceux imprimés sur le formulaire lui-même. Ils servent à
 * cocher les cases et à exiger la signature du détenteur ; ils ne remplacent
 * pas le jugement de l'opérateur, qui peut corriger le fluide ou la catégorie
 * ADR avant validation.
 */

export const CERFA_FORM_VERSION = '15497*04';

/** Nature de l'intervention (case 4) : plusieurs cases possibles. */
export const CERFA_NATURES = [
  { key: 'natureAssembly', label: 'Assemblage de l’équipement' },
  { key: 'natureCommissioning', label: 'Mise en service de l’équipement' },
  { key: 'natureModification', label: 'Modification de l’équipement' },
  { key: 'natureMaintenance', label: 'Maintenance de l’équipement' },
  { key: 'naturePeriodicCheck', label: 'Contrôle d’étanchéité périodique' },
  { key: 'natureNonPeriodicCheck', label: 'Contrôle d’étanchéité non périodique' },
  { key: 'natureDismantling', label: 'Démantèlement' },
  { key: 'natureOther', label: 'Autre' },
] as const;
export type CerfaNatureKey = (typeof CERFA_NATURES)[number]['key'];

/** Catégorie ADR/RID du fluide récupéré (case 12). */
export const CERFA_ADR_CATEGORIES = [
  { key: 'UN1078', label: 'UN 1078, déchet gaz frigorifique NSA, 2.2 (C/E) — non inflammable', flammable: false },
  { key: 'autre_non_inflammable', label: 'Autre fluide non inflammable (rubrique 14 06 01*)', flammable: false },
  { key: 'UN3161', label: 'UN 3161, déchet gaz liquéfié inflammable NSA, 2.1 (B/D)', flammable: true },
  { key: 'autre_inflammable', label: 'Autre fluide inflammable (rubrique 16 05 04*)', flammable: true },
] as const;
export type CerfaAdrCategory = (typeof CERFA_ADR_CATEGORIES)[number]['key'];

export const CERFA_LEAK_SLOTS = [1, 2, 3] as const;

/**
 * Clés du contenu d'une fiche (valeurs texte, nombre ou booléen). Les
 * quantités sont en kilogrammes, saisies avec une virgule française.
 */
export const CERFA_CONTENT_LABELS: Record<string, string> = {
  ...Object.fromEntries(CERFA_NATURES.map((nature) => [nature.key, nature.label])),
  natureOtherText: 'Autre nature (préciser)',
  detectorId: 'Détecteur manuel de fuite',
  detectorCheckedOn: 'Détecteur contrôlé le',
  leaksFound: 'Fuites constatées',
  leak1Location: 'Fuite 1 — localisation',
  leak1Repair: 'Fuite 1 — réparation',
  leak2Location: 'Fuite 2 — localisation',
  leak2Repair: 'Fuite 2 — réparation',
  leak3Location: 'Fuite 3 — localisation',
  leak3Repair: 'Fuite 3 — réparation',
  qtyVirginKg: 'A — fluide vierge chargé (kg)',
  qtyRecycledKg: 'B — fluide recyclé chargé (kg)',
  qtyRegeneratedKg: 'C — fluide régénéré chargé (kg)',
  qtyTreatmentKg: 'D — fluide récupéré destiné au traitement (kg)',
  qtyReuseKg: 'E — fluide récupéré conservé pour réutilisation (kg)',
  chargedFluidIfChanged: 'Fluide chargé si changement',
  bsffNumber: 'Numéro du BSFF (Trackdéchets)',
  containerIds: 'Contenant(s) du fluide récupéré',
  adrCategory: 'Dénomination ADR/RID',
  adrOtherText: 'Autre fluide (préciser)',
  destination: 'Installation de destination du fluide récupéré',
};

export type CerfaFamily = 'HCFC' | 'HFC' | 'HFO';

/** Normalise « r-1234ze(E) », « R 32 » ou « R32 » en « R-1234ZE(E) », « R-32 ». */
export function normalizeFluidCode(code: string | null | undefined): string {
  const compact = (code ?? '').toUpperCase().replace(/\s+/g, '');
  const match = /^R-?(.+)$/.exec(compact);
  return match ? `R-${match[1]}` : compact;
}

/** Fluides inflammables courants (classes A2L, A2 et A3). */
const FLAMMABLE = new Set([
  'R-32', 'R-143A', 'R-152A', 'R-1234YF', 'R-1234ZE', 'R-1234ZE(E)', 'R-444B', 'R-447A', 'R-447B',
  'R-452B', 'R-454A', 'R-454B', 'R-454C', 'R-455A', 'R-457A', 'R-290', 'R-600A', 'R-1270',
]);

/**
 * Famille d'un fluide sur le formulaire. Les PFC partagent la ligne « HFC / PFC »
 * du cadre 7 ; les CFC sont dans le champ du formulaire mais n'y ont pas de ligne.
 */
export type CerfaFluidFamily = CerfaFamily | 'CFC';
type Component = CerfaFluidFamily | 'PFC' | 'NONE';

/**
 * Corps purs, par numéro ASHRAE 34 / ISO 817 sans préfixe ni isomère. Sources :
 * désignations et formules de la liste ASHRAE (norme 34-2022) ; HFC et PFC de
 * l'annexe I, HFO et HCFO de l'annexe II du règlement (UE) 2024/573 ; CFC et HCFC
 * cités par la notice du formulaire. `NONE` : ni fluor ni chlore fluoré
 * (hydrocarbures, éthers, composés inorganiques), donc hors du formulaire.
 * Les halons, le R-13I1 et les solvants chlorés ne sont pas classés.
 */
const PURE: Record<Component, readonly string[]> = {
  CFC: ['11', '12', '13', '113', '114', '115'],
  HCFC: ['21', '22', '31', '123', '124', '141B', '142B'],
  HFC: [
    '23', '32', '41', '125', '134', '134A', '143', '143A', '152', '152A', '161', '227EA', '236CB', '236EA',
    '236FA', '245CA', '245FA', '365MFC',
  ],
  PFC: ['14', '116', '218', 'C318'],
  HFO: ['1132', '1132A', '1224YD', '1233ZD', '1234YF', '1234ZE', '1336MZZ'],
  NONE: [
    '50', '170', '290', '600', '600A', '601', '601A', 'E170', '610', '611', '630', '631', '702', '704', '717',
    '718', '720', '728', '732', '740', '744', '744A', '764', '1150', '1270',
  ],
};
const COMPONENT = new Map<string, Component>(
  (Object.entries(PURE) as [Component, readonly string[]][]).flatMap(([family, codes]) =>
    codes.map((code) => [code, family] as const)),
);

/**
 * Mélanges zéotropes (R-4xx) et azéotropes (R-5xx) : composition relevée telle
 * quelle sur la liste « ASHRAE Refrigerant Designations » (norme 34-2022, liste
 * mise à jour le 25/05/2021). Un mélange plus récent absent de cette liste reste
 * de famille inconnue : aucun code n'est deviné.
 */
const BLENDS: Record<string, string> = {
  '400': '12/114', '401A': '22/152A/124', '401B': '22/152A/124', '401C': '22/152A/124', '402A': '125/290/22',
  '402B': '125/290/22', '403A': '290/22/218', '403B': '290/22/218', '404A': '125/143A/134A',
  '405A': '22/152A/142B/C318', '406A': '22/600A/142B', '407A': '32/125/134A', '407B': '32/125/134A',
  '407C': '32/125/134A', '407D': '32/125/134A', '407E': '32/125/134A', '407F': '32/125/134A',
  '407G': '32/125/134A', '407H': '32/125/134A', '407I': '32/125/134A', '408A': '125/143A/22',
  '409A': '22/124/142B', '409B': '22/124/142B', '410A': '32/125', '410B': '32/125', '411A': '1270/22/152A',
  '411B': '1270/22/152A', '412A': '22/218/143B', '413A': '218/134A/600A', '414A': '22/124/600A/142B',
  '414B': '22/124/600A/142B', '415A': '22/152A', '415B': '22/152A', '416A': '134A/124/600',
  '417A': '125/134A/600', '417B': '125/134A/600', '417C': '125/134A/600', '418A': '290/22/152A',
  '419A': '125/134A/E170', '419B': '125/134A/E170', '420A': '134A/142B', '421A': '125/134A', '421B': '125/134A',
  '422A': '125/134A/600A', '422B': '125/134A/600A', '422C': '125/134A/600A', '422D': '125/134A/600A',
  '422E': '125/134A/600A', '423A': '134A/227EA', '424A': '125/134A/600A/600/601A', '425A': '32/134A/227EA',
  '426A': '125/134A/600/601A', '427A': '32/125/143A/134A', '427C': '32/125/143A/134A',
  '428A': '125/143A/290/600A', '429A': 'E170/152A/600A', '430A': '152A/600A', '431A': '290/152A',
  '432A': '1270/E170', '433A': '1270/290', '433B': '1270/290', '433C': '1270/290', '434A': '125/143A/134A/600A',
  '435A': 'E170/152A', '436A': '290/600A', '436B': '290/600A', '436C': '290/600A', '437A': '125/134A/600/601',
  '438A': '32/125/134A/600/601A', '439A': '32/125/600A', '440A': '290/134A/152A', '441A': '170/290/600A/600',
  '442A': '32/125/134A/152A/227EA', '443A': '1270/290/600A', '444A': '32/152A/1234ZE', '444B': '32/152A/1234ZE',
  '445A': '744/134A/1234ZE', '446A': '32/1234ZE/600', '447A': '32/125/1234ZE', '447B': '32/125/1234ZE',
  '448A': '32/125/1234YF/134A/1234ZE', '448B': '32/125/1234YF/134A/1234ZE', '449A': '32/125/1234YF/134A',
  '449B': '32/125/1234YF/134A', '449C': '32/125/1234YF/134A', '450A': '134A/1234ZE', '451A': '1234YF/134A',
  '451B': '1234YF/134A', '452A': '32/125/1234YF', '452B': '32/125/1234YF', '452C': '32/125/1234YF',
  '453A': '32/125/134A/227EA/600/601A', '454A': '32/1234YF', '454B': '32/1234YF', '454C': '32/1234YF',
  '455A': '744/32/1234YF', '456A': '32/134A/1234ZE', '457A': '32/1234YF/152A', '457B': '32/1234YF/152A',
  '457C': '32/1234YF/152A', '458A': '32/125/134A/227EA/236FA', '459A': '32/1234YF/1234ZE',
  '459B': '32/1234YF/1234ZE', '460A': '32/125/134A/1234ZE', '460B': '32/125/134A/1234ZE',
  '460C': '32/125/134A/1234ZE', '461A': '125/143A/134A/227EA/600A', '462A': '32/125/143A/134A/600',
  '463A': '744/32/125/1234YF/134A', '464A': '32/125/1234ZE/227EA', '465A': '32/290/1234YF',
  '466A': '32/125/13I1', '467A': '32/125/134A/600A', '468A': '1132A/32/1234YF', '468B': '1132A/32/1234YF',
  '468C': '1132A/32/1234YF', '469A': '744/32/125', '470A': '744/32/125/134A/1234ZE/227EA',
  '470B': '744/32/125/134A/1234ZE/227EA', '471A': '1234ZE/227EA/1336MZZ', '472A': '744/32/134A',
  '472B': '744/32/134A', '473A': '1132A/23/744/125', '474A': '1132/1234YF', '475A': '1234YF/134A/1234ZE',
  '476A': '134A/1234ZE/1336MZZ', '500': '12/152A', '501': '22/12', '502': '22/115', '503': '23/13',
  '504': '32/115', '505': '12/31', '506': '31/114', '507A': '125/143A', '508A': '23/116', '508B': '23/116',
  '509A': '22/218', '510A': 'E170/600A', '511A': '290/E170', '512A': '134A/152A', '513A': '1234YF/134A',
  '513B': '1234YF/134A', '514A': '1336MZZ/1130', '515A': '1234ZE/227EA', '515B': '1234ZE/227EA',
  '516A': '1234YF/134A/152A',
};

/** Désignations courantes sans numéro. */
const ALIASES: Record<string, string> = {
  CO2: '744', NH3: '717', AMMONIAC: '717', PROPANE: '290', ISOBUTANE: '600A', BUTANE: '600',
};

/**
 * Numéro du fluide sans préfixe (« R », « R- », « HFC- », « HCFC- », « HFO- »,
 * « CFC- »…), sans espace ni isomère : « hfc-134a », « R 134a » et « R134A »
 * donnent « 134A » ; « R-1234ze(E) » donne « 1234ZE ».
 */
export function fluidNumber(code: string | null | undefined): string {
  const compact = (code ?? '').toUpperCase().replace(/[\s_]+/g, '');
  const bare = compact.replace(/^(?:HCFC|HCFO|HFC|HFO|CFC|PFC|HC|R)-?(?=[A-Z]?\d)/, '').replace(/\((?:E|Z)\)$/, '');
  return ALIASES[bare] ?? bare;
}

/**
 * Famille d'un mélange : celle de son composant le plus contraint, CFC, puis
 * HCFC, puis HFC ou PFC, puis HFO. La notice du formulaire le pose pour un
 * mélange HFC/HFO (« doit être considéré comme relevant de la catégorie des
 * HFC ») ; MyEquip applique le même ordre aux mélanges contenant un CFC ou un HCFC.
 */
function blendFamily(composition: string): CerfaFluidFamily | null {
  const families = composition.split('/').map((component) => COMPONENT.get(component));
  if (families.includes('CFC')) return 'CFC';
  if (families.includes('HCFC')) return 'HCFC';
  if (families.includes('HFC') || families.includes('PFC')) return 'HFC';
  if (families.includes('HFO')) return 'HFO';
  return null;
}

/**
 * Place d'un fluide vis-à-vis du formulaire : `fluore` (concerné, famille
 * connue), `hors_champ` (fluide non fluoré : pas de fiche CERFA), `inconnu`
 * (rien ne permet de le classer : jamais supposé HFC).
 */
export type CerfaFluidScope = 'fluore' | 'hors_champ' | 'inconnu';
export interface CerfaFluidClass {
  scope: CerfaFluidScope;
  family: CerfaFluidFamily | null;
  /**
   * Désignation incomplète : « R-404 » peut être plusieurs mélanges, la lettre
   * n'est jamais devinée. Exemple de désignation complète à proposer.
   */
  suggestion?: string;
}

const fluorinated = (family: CerfaFluidFamily): CerfaFluidClass => ({ scope: 'fluore', family });

/**
 * Classe un fluide d'après sa désignation. À défaut, la catégorie déclarée par
 * l'entreprise en ajoutant le fluide à son référentiel est retenue (« naturel » :
 * non fluoré). Une désignation incomplète reste inconnue, quelle que soit la
 * catégorie déclarée : elle doit d'abord être corrigée.
 */
export function classifyFluid(code: string | null | undefined, declared?: string | null): CerfaFluidClass {
  const number = fluidNumber(code);
  const pure = COMPONENT.get(number);
  if (pure === 'NONE') return { scope: 'hors_champ', family: null };
  if (pure) return fluorinated(pure === 'PFC' ? 'HFC' : pure);
  const composition = BLENDS[number];
  if (composition) {
    const family = blendFamily(composition);
    return family ? fluorinated(family) : { scope: 'hors_champ', family: null };
  }
  // « R-404 » ou « R-507 » : la lettre du mélange manque.
  if (/^[45]\d\d$/.test(number) && BLENDS[`${number}A`]) {
    return { scope: 'inconnu', family: null, suggestion: `R-${number}A` };
  }
  const category = (declared ?? '').toUpperCase();
  if (category === 'CFC' || category === 'HCFC' || category === 'HFO' || category === 'HFC') return fluorinated(category);
  if (category === 'PFC') return fluorinated('HFC');
  if (category === 'NATUREL') return { scope: 'hors_champ', family: null };
  return { scope: 'inconnu', family: null };
}

/** Famille réglementaire du fluide, `null` si elle est inconnue ou hors du formulaire. */
export function fluidFamily(code: string | null | undefined, declared?: string | null): CerfaFluidFamily | null {
  return classifyFluid(code, declared).family;
}

/** Nombre de désignations reconnues par famille, pour la documentation et les tests. */
export function knownFluidCounts(): Record<CerfaFluidFamily | 'hors_champ', number> {
  const counts = { CFC: 0, HCFC: 0, HFC: 0, HFO: 0, hors_champ: 0 };
  for (const number of [...COMPONENT.keys(), ...Object.keys(BLENDS)]) {
    const { scope, family } = classifyFluid(number);
    if (scope === 'hors_champ') counts.hors_champ += 1;
    else if (family) counts[family] += 1;
  }
  return counts;
}

export function isFlammableFluid(code: string | null | undefined): boolean {
  return FLAMMABLE.has(normalizeFluidCode(code));
}

export function defaultAdrCategory(code: string | null | undefined): CerfaAdrCategory {
  return isFlammableFluid(code) ? 'UN3161' : 'UN1078';
}

export interface CerfaThresholds {
  family: CerfaFluidFamily | null;
  /** Colonne cochée dans la case 7 (0, 1 ou 2), `null` sous le premier seuil. */
  column: 0 | 1 | 2 | null;
  /** Ligne de la fréquence de contrôle : 8 (sans détection permanente) ou 9 (avec). */
  frequencyRow: 8 | 9 | null;
  frequencyMonths: number | null;
  /** Signature du détenteur exigée à la validation. */
  holderSignatureRequired: boolean;
  /**
   * La signature est exigée par prudence : famille du fluide inconnue ou tonnage
   * d'un HFC non calculé, donc impossible de prouver que le seuil n'est pas dépassé.
   */
  holderSignaturePrecaution: boolean;
}

const BOUNDS: Record<CerfaFamily, readonly [number, number, number]> = {
  HCFC: [2, 30, 300], // kg
  HFC: [5, 50, 500], // t éq. CO2
  HFO: [1, 10, 100], // kg
};
const MONTHS: Record<8 | 9, readonly [number, number, number]> = {
  8: [12, 6, 3],
  9: [24, 12, 6],
};

/**
 * Cases 7 à 9 et signature du détenteur pour une charge donnée. La détection
 * permanente inconnue (`null`) ne coche aucune fréquence : le formulaire ne doit
 * rien affirmer qui n'a pas été renseigné.
 */
export function cerfaThresholds(input: {
  family: CerfaFluidFamily | null;
  chargeKg: number | null;
  teqCo2: number | null;
  permanentDetection: boolean | null;
}): CerfaThresholds {
  const { family } = input;
  // Famille inconnue, ou CFC (sans ligne au cadre 7 ni seuil dans l'article R. 543-82) :
  // aucune case affirmée, et la signature du détenteur est demandée plutôt que d'en
  // dispenser à tort.
  if (family === null || family === 'CFC') {
    return {
      family, column: null, frequencyRow: null, frequencyMonths: null,
      holderSignatureRequired: true, holderSignaturePrecaution: true,
    };
  }
  const value = family === 'HFC' ? input.teqCo2 : input.chargeKg;
  const [low, mid, high] = BOUNDS[family];
  const column =
    value === null || !Number.isFinite(value) || value < low
      ? null
      : value < mid
        ? 0
        : value < high
          ? 1
          : 2;
  // La détection permanente n'allonge la périodicité que pour les HFC et HFO.
  const frequencyRow =
    column === null || input.permanentDetection === null
      ? null
      : input.permanentDetection && family !== 'HCFC'
        ? 9
        : 8;
  // Sans PRP, le tonnage calculé d'un HFC vaut zéro : le seuil de 5 t ne peut pas être écarté.
  const tonnageUnknown = family === 'HFC' && !((input.teqCo2 ?? 0) > 0);
  const holderSignatureRequired =
    family === 'HCFC'
      ? (input.chargeKg ?? 0) > 3
      : family === 'HFC'
        ? tonnageUnknown || (input.teqCo2 ?? 0) > 5
        : false;
  return {
    family,
    column,
    frequencyRow,
    frequencyMonths: column === null || frequencyRow === null ? null : MONTHS[frequencyRow][column],
    holderSignatureRequired,
    holderSignaturePrecaution: tonnageUnknown,
  };
}

/** Quantité en kilogrammes saisie « 1,5 » ou « 1.5 » ; `null` si vide ou invalide. */
export function parseKg(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? value : null;
  if (typeof value !== 'string' || !value.trim()) return null;
  const normalized = value.trim().replace(/\s/g, '').replace(',', '.');
  if (!/^\d+(\.\d+)?$/.test(normalized)) return null;
  return Number(normalized);
}

/** Kilogrammes imprimés à la française, trois décimales au plus (« 1,25 »). */
export function formatKg(value: number | null): string {
  if (value === null) return '';
  return value.toLocaleString('fr-FR', { maximumFractionDigits: 3, useGrouping: false });
}

/** Numéro de fiche « AAAA-NNNN » (au moins quatre chiffres). */
export function formatCerfaNumber(year: number, sequence: number): string {
  return `${year}-${String(sequence).padStart(4, '0')}`;
}

/**
 * Révision des règles de remplissage figée avec chaque fiche validée. Une fiche
 * validée avant une révision garde les règles de son époque : son PDF se
 * régénère à l'identique.
 *
 * - 1 (absente) : cadres 12 et 13 remplis dès qu'un fluide est récupéré ; date
 *   de signature seule dans le cartouche.
 * - 2 : cadres 12 et 13 remplis seulement quand l'intervention produit un déchet
 *   (D > 0) ; date de l'intervention dans le cartouche ; contenants repris du
 *   registre ; liste de contenants trop longue reportée en observations ; tonnage
 *   équivalent CO2 nul (PRP inconnu) laissé vide ; famille inconnue sans case cochée.
 */
export const CERFA_RULES_REVISION = 2;

/** Quantités de la case 11, en kilogrammes, registre repris à défaut de détail saisi. */
export interface CerfaQuantities {
  /** A, B, C. */
  charged: [number | null, number | null, number | null];
  /** D, E. */
  recovered: [number | null, number | null];
  chargedTotal: number | null;
  recoveredTotal: number | null;
  /** D : fluide destiné au traitement, c'est-à-dire le déchet produit. */
  wasteKg: number;
}

function sumKg(values: (number | null)[]): number | null {
  const known = values.filter((value): value is number => value !== null);
  // Arrondi au gramme : 0,1 + 0,2 ne doit pas s'imprimer 0,30000000000000004.
  return known.length ? Math.round(known.reduce((total, value) => total + value, 0) * 1000) / 1000 : null;
}

/**
 * Case 11. Sans détail saisi, le registre fournit le total : chargé = vierge
 * (A), récupéré = destiné au traitement (D), comme la fiche le propose par défaut.
 */
export function cerfaQuantities(
  content: Record<string, unknown>,
  register: { chargedKg: number | null; recoveredKg: number | null },
): CerfaQuantities {
  const charged: CerfaQuantities['charged'] = [
    parseKg(content.qtyVirginKg), parseKg(content.qtyRecycledKg), parseKg(content.qtyRegeneratedKg),
  ];
  const recovered: CerfaQuantities['recovered'] = [parseKg(content.qtyTreatmentKg), parseKg(content.qtyReuseKg)];
  if (sumKg(charged) === null) charged[0] = register.chargedKg;
  if (sumKg(recovered) === null) recovered[0] = register.recoveredKg;
  return {
    charged, recovered,
    chargedTotal: sumKg(charged),
    recoveredTotal: sumKg(recovered),
    wasteKg: recovered[0] ?? 0,
  };
}

export interface CerfaIssue {
  /** Identifiant stable, pour les tests et l'interface. */
  code: string;
  /** Cadre du formulaire concerné : « 1 » à « 14 », ou « signature ». */
  frame: string;
  /** Ce qui manque et où le corriger, en français. */
  message: string;
}

export interface CerfaReadinessInput {
  /** Contenu saisi sur la fiche. */
  content: Record<string, unknown>;
  operator: { name: string; address: string; siret: string };
  /** N° d'attestation de capacité effectivement imprimé (fiche, sinon réglage). */
  capacityCertificate: string;
  holder: { name: string; address: string; siret: string; professional: boolean };
  equipment: {
    /** Marque ou modèle renseigné. */
    identified: boolean;
    fluid: string;
    scope: CerfaFluidScope;
    family: CerfaFluidFamily | null;
    /** Désignation complète à proposer quand celle de la machine est ambiguë (« R-404 »). */
    suggestion?: string | undefined; // exactOptionalPropertyTypes: absent or explicit undefined. skipcq: JS-T1001
    chargeKg: number | null;
    teqCo2: number | null;
    permanentDetection: boolean | null;
  };
  register: {
    chargedKg: number | null;
    recoveredKg: number | null;
    containers: string[];
    /** Autres fluides inscrits au registre de l'intervention : non repris sur la fiche. */
    otherFluids?: string[] | undefined; // exactOptionalPropertyTypes: absent or explicit undefined. skipcq: JS-T1001
  };
  /** Date du jour à Paris, « AAAA-MM-JJ ». */
  today: string;
}

export interface CerfaReadiness {
  /** Empêche la validation : le formulaire serait incomplet ou incohérent. */
  blocking: CerfaIssue[];
  /** À vérifier : la validation reste possible. */
  warnings: CerfaIssue[];
}

const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const QUANTITY_KEYS = ['qtyVirginKg', 'qtyRecycledKg', 'qtyRegeneratedKg', 'qtyTreatmentKg', 'qtyReuseKg'] as const;

/** Date « AAAA-MM-JJ » réelle (le 31 février n'en est pas une). */
export function isIsoDate(value: string): boolean {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (!match) return false;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  return date.getUTCMonth() === Number(match[2]) - 1 && date.getUTCDate() === Number(match[3]);
}

/**
 * Ce qui manque à une fiche avant sa validation. La notice 52064#04 demande que
 * les cadres 1 à 4 et le cartouche de signature soient toujours remplis, les
 * cadres 5 à 11 dès qu'une intervention a eu lieu, les cadres 12 et 13 quand
 * l'intervention produit un déchet de fluide.
 */
export function cerfaReadiness(input: CerfaReadinessInput): CerfaReadiness {
  const blocking: CerfaIssue[] = [];
  const warnings: CerfaIssue[] = [];
  const block = (code: string, frame: string, message: string): void => { blocking.push({ code, frame, message }); };
  const warn = (code: string, frame: string, message: string): void => { warnings.push({ code, frame, message }); };
  const { content, operator, holder, equipment } = input;

  // [1] Opérateur.
  if (!operator.name.trim()) block('operator_name', '1', 'Nom de votre entreprise manquant (Mon entreprise › Identité).');
  // Le SIRET de l'opérateur est une mention obligatoire du cadre 1 (mise en route,
  // 18/10/2026) : la fiche reste un brouillon modifiable tant qu'il manque, et la
  // validation reprend dès que le patron l'a renseigné. Les autres réglages du
  // responsable n'arrêtent pas le technicien : la case est imprimée vide.
  if (operator.siret.trim() && !/^\d{14}$/.test(operator.siret.replace(/\s/g, ''))) {
    block('operator_siret', '1', 'SIRET de l’entreprise émettrice incomplet : 14 chiffres attendus.');
  } else if (!operator.siret.trim()) {
    block('operator_siret', '1',
      'SIRET de votre entreprise manquant : la fiche reste en brouillon. Le patron l’ajoute dans Mon entreprise › Identité et coordonnées.');
  }
  if (!operator.address.trim()) warn('operator_address', '1', 'Adresse de votre entreprise manquante (Mon entreprise › Identité).');
  if (!input.capacityCertificate.trim()) {
    warn('capacity_certificate', '1',
      'N° d’attestation de capacité manquant : la case restera vide sur le CERFA. Saisissez-le sur la fiche, ou votre responsable le renseigne dans Mon entreprise › Fluides et CERFA.');
  }

  // [2] Détenteur.
  if (!holder.name.trim()) block('holder_name', '2', 'Nom du détenteur manquant sur la fiche du client.');
  if (!holder.address.trim()) warn('holder_address', '2', 'Adresse du détenteur manquante sur la fiche du client.');
  if (holder.professional && !holder.siret.trim()) {
    warn('holder_siret', '2',
      'SIRET du détenteur manquant sur la fiche du client. S’il n’en a pas, notez en observations son numéro d’association ou de TVA.');
  }

  // [3] Équipement.
  if (!equipment.identified) warn('equipment_identification', '3', 'Marque et modèle de la machine non renseignés.');
  if (!equipment.fluid.trim()) block('equipment_fluid', '3', 'Fluide de la machine non renseigné.');
  if (equipment.chargeKg === null || equipment.chargeKg <= 0) block('equipment_charge', '3', 'Charge totale de la machine non renseignée.');
  if (equipment.fluid.trim() && equipment.scope === 'hors_champ') {
    block('out_of_scope', '3',
      'Ce fluide n’est pas concerné par le CERFA 15497, réservé aux fluides fluorés. Consignez la manipulation dans le registre des fluides.');
  }
  if (equipment.fluid.trim() && equipment.scope === 'inconnu') {
    warn('fluid_family', '7', equipment.suggestion
      ? `Fluide « ${equipment.fluid.trim()} » imprécis : indiquez le fluide exact sur la fiche de la machine (par exemple ${equipment.suggestion}). En attendant, les cadres 7 à 9 resteront vides et la signature du détenteur est demandée par prudence.`
      : 'Famille du fluide inconnue : les cadres 7 à 9 resteront vides et la signature du détenteur est demandée par prudence. Vérifiez le fluide sur la fiche de la machine.');
  }
  if (equipment.family === 'CFC') {
    warn('cfc', '7',
      'CFC : le cadre 7 n’a pas de ligne pour cette famille, il restera vide. La signature du détenteur est demandée par prudence.');
  }
  // Sans PRP, le tonnage calculé vaut zéro : le seuil de signature ne peut pas être écarté.
  if (equipment.fluid.trim() && equipment.family === 'HFC' && !((equipment.teqCo2 ?? 0) > 0)) {
    warn('equipment_teq', '3',
      'PRG du fluide inconnu : le tonnage équivalent CO2 et les cadres 7 à 9 resteront vides, et la signature du détenteur est demandée par prudence. Indiquez le PRG sur la fiche de la machine.');
  }

  // [4] Nature de l'intervention et date.
  if (!CERFA_NATURES.some((nature) => content[nature.key] === true)) {
    block('nature', '4', 'Cochez au moins une nature d’intervention.');
  }
  if (content.natureOther === true && !str(content.natureOtherText)) {
    block('nature_other', '4', 'Précisez la nature « Autre ».');
  }
  const operationDate = str(content.operationDate);
  // Sans date saisie, le cartouche porte la date de signature.
  if (operationDate && !isIsoDate(operationDate)) block('operation_date', 'signature', 'Date de l’intervention invalide.');
  else if (operationDate > input.today) block('operation_date_future', 'signature', 'La date de l’intervention est dans le futur.');

  // [5] à [10] Contrôle d'étanchéité.
  const leakCheck = content.naturePeriodicCheck === true || content.natureNonPeriodicCheck === true;
  const leaks = str(content.leaksFound);
  if (leakCheck && leaks !== 'oui' && leaks !== 'non') {
    block('leaks_result', '10', 'Indiquez si le contrôle d’étanchéité a constaté des fuites.');
  }
  if (!leakCheck && (leaks === 'oui' || leaks === 'non')) {
    warn('leaks_without_check', '4', 'Un résultat de contrôle est saisi sans nature « Contrôle d’étanchéité » cochée.');
  }
  if (leakCheck && !str(content.detectorId)) warn('detector', '5', 'Détecteur manuel de fuite non identifié.');
  const detectorDate = str(content.detectorCheckedOn);
  if (str(content.detectorId) && !detectorDate) warn('detector_date', '5', 'Date du dernier contrôle du détecteur manquante.');
  if (detectorDate && !isIsoDate(detectorDate)) block('detector_date_invalid', '5', 'Date de contrôle du détecteur invalide.');
  if (equipment.permanentDetection === null) {
    warn('permanent_detection', '6',
      'Détection permanente des fuites non renseignée sur la machine : la case 6 et la fréquence resteront vides.');
  }
  if (leaks === 'oui') {
    const located = CERFA_LEAK_SLOTS.filter((slot) => str(content[`leak${slot}Location`]));
    if (located.length === 0) block('leak_location', '10', 'Localisez au moins une fuite constatée.');
    for (const slot of located) {
      const repair = str(content[`leak${slot}Repair`]);
      if (repair !== 'realisee' && repair !== 'a_faire') {
        block(`leak${slot}_repair`, '10', `Fuite ${slot} : indiquez si la réparation est réalisée ou à faire.`);
      }
    }
  }

  // [11] à [13] Manipulation du fluide.
  if (QUANTITY_KEYS.some((key) => str(content[key]) && parseKg(content[key]) === null)) {
    block('quantities', '11', 'Quantités invalides : saisissez des kilogrammes, par exemple 1,5.');
  }
  const otherFluids = input.register.otherFluids ?? [];
  if (otherFluids.length > 0) {
    warn('register_other_fluid', '11',
      `Le registre de l’intervention contient aussi du ${otherFluids.join(', ')} : ces quantités ne sont pas reprises sur cette fiche (fluide de la machine : ${equipment.fluid || 'non renseigné'}).`);
  }
  const quantities = cerfaQuantities(content, input.register);
  if ((quantities.recoveredTotal ?? 0) > 0 && !str(content.containerIds) && input.register.containers.length === 0) {
    warn('containers', '11', 'Contenant du fluide récupéré non identifié.');
  }
  if (quantities.wasteKg > 0) {
    const adr = str(content.adrCategory);
    if (!CERFA_ADR_CATEGORIES.some((category) => category.key === adr)) {
      block('adr', '12', 'Choisissez la dénomination ADR/RID du fluide destiné au traitement.');
    } else if (adr.startsWith('autre') && !str(content.adrOtherText)) {
      block('adr_other', '12', 'Précisez le fluide de la dénomination ADR/RID « Autre ».');
    }
    if (!str(content.destination)) warn('destination', '13', 'Installation de destination du fluide récupéré non renseignée.');
  }
  return { blocking, warnings };
}

/** Texte comparable sans accents ni casse : « Supérette » se trouve en tapant « superette ». */
export function foldSearchText(value: string): string {
  return value.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();
}
